import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock } from './CodeBlock';
import { Bot, User, Copy, Check, Download, FileText, FileSpreadsheet, Image as ImageIcon, File, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function ChatMessage({ message, theme }) {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      toast.success('In Zwischenablage kopiert');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    }
  };

  const handleDownloadMd = () => {
    try {
      // Create filename from first line or timestamp
      const firstLine = message.content.split('\n')[0].replace(/[#*]/g, '').trim();
      const filename = firstLine.slice(0, 50).replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '').trim() || 'antwort';
      const timestamp = new Date().toISOString().slice(0, 10);
      
      // Create blob and download
      const blob = new Blob([message.content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}_${timestamp}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Markdown heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const handleOpenInBear = () => {
    try {
      // Create title from first line
      const firstLine = message.content.split('\n')[0].replace(/[#*]/g, '').trim();
      const title = firstLine.slice(0, 100) || 'Orq.ai Antwort';
      
      // Bear URL scheme: bear://x-callback-url/create?title=TITLE&text=CONTENT
      const bearUrl = `bear://x-callback-url/create?title=${encodeURIComponent(title)}&text=${encodeURIComponent(message.content)}&tags=${encodeURIComponent('orq-ai,sap')}`;
      
      // Open Bear
      window.location.href = bearUrl;
      
      toast.success('In Bear öffnen...');
    } catch (error) {
      toast.error('Fehler beim Öffnen in Bear');
    }
  };

  return (
    <div
      data-testid={`message-${message.role}`}
      className={`group flex gap-2 md:gap-4 py-4 md:py-6 px-2 md:px-4 ${isUser ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
    >
      {!isUser && (
        <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
          <Bot className="h-4 w-4 md:h-5 md:w-5 text-primary-foreground" />
        </div>
      )}
      
      <div className={`flex flex-col gap-2 ${isUser ? 'items-end' : 'items-start'} max-w-[95%] md:max-w-[85%] min-w-0`}>
        {isUser ? (
          <div className="relative bg-secondary text-secondary-foreground rounded-2xl rounded-tr-sm px-3 md:px-4 py-2 md:py-2.5">
            <p className="text-sm leading-relaxed break-words">{message.content}</p>
            {message.file && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-background/50 rounded-lg border border-border/50">
                {message.file.type?.includes('image') ? (
                  <ImageIcon className="h-4 w-4 text-blue-500" />
                ) : message.file.type?.includes('spreadsheet') || message.file.name?.endsWith('.csv') || message.file.name?.endsWith('.xlsx') ? (
                  <FileSpreadsheet className="h-4 w-4 text-green-500" />
                ) : message.file.type?.includes('pdf') ? (
                  <FileText className="h-4 w-4 text-red-500" />
                ) : (
                  <File className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {message.file.name}
                </span>
              </div>
            )}
            {/* Multiple files display */}
            {message.files && message.files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {message.files.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-background/50 rounded-lg border border-border/50">
                    {file.type?.includes('image') ? (
                      <ImageIcon className="h-4 w-4 text-blue-500" />
                    ) : file.type?.includes('spreadsheet') || file.name?.endsWith('.csv') || file.name?.endsWith('.xlsx') ? (
                      <FileSpreadsheet className="h-4 w-4 text-green-500" />
                    ) : file.type?.includes('pdf') ? (
                      <FileText className="h-4 w-4 text-red-500" />
                    ) : (
                      <File className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="text-xs text-muted-foreground truncate max-w-[150px]" title={file.name}>
                      {file.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Button
              data-testid="copy-user-message-btn"
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="absolute -top-2 -right-2 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity bg-background border border-border shadow-sm"
            >
              {copied ? (
                <><Check className="h-3 w-3 mr-1" /> Kopiert</>
              ) : (
                <><Copy className="h-3 w-3 mr-1" /> Kopieren</>
              )}
            </Button>
          </div>
        ) : (
          <div className="relative prose prose-sm dark:prose-invert max-w-none overflow-x-auto">
            <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <Button
                data-testid="download-md-btn"
                variant="ghost"
                size="sm"
                onClick={handleDownloadMd}
                className="h-7 px-2 bg-background border border-border shadow-sm"
                title="Als Markdown herunterladen"
              >
                <Download className="h-3 w-3 mr-1" /> .md
              </Button>
              <Button
                data-testid="copy-assistant-message-btn"
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="h-7 px-2 bg-background border border-border shadow-sm"
              >
                {copied ? (
                  <><Check className="h-3 w-3 mr-1" /> Kopiert</>
                ) : (
                  <><Copy className="h-3 w-3 mr-1" /> Kopieren</>
                )}
              </Button>
            </div>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const codeValue = String(children).replace(/\n$/, '');
                  
                  return !inline && match ? (
                    <CodeBlock
                      language={match[1]}
                      value={codeValue}
                      theme={theme}
                    />
                  ) : (
                    <code className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-xs" {...props}>
                      {children}
                    </code>
                  );
                },
                p: ({ children }) => <p className="mb-4 last:mb-0 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                li: ({ children }) => <li className="text-sm">{children}</li>,
                h1: ({ children }) => <h1 className="text-2xl font-semibold mb-3 mt-6">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl font-semibold mb-2 mt-5">{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 mt-4">{children}</h3>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-border pl-4 italic my-4">{children}</blockquote>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full border-collapse border border-border text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => <tr className="border-b border-border">{children}</tr>,
                th: ({ children }) => <th className="px-4 py-2 text-left font-semibold border border-border">{children}</th>,
                td: ({ children }) => <td className="px-4 py-2 border border-border">{children}</td>,
              }}
            >
              {message.content}
            </ReactMarkdown>
            
            {/* Bottom action buttons for long responses */}
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mt-3 md:mt-4 pt-2 md:pt-3 border-t border-border/50">
              <Button
                data-testid="open-bear-btn"
                variant="outline"
                size="sm"
                onClick={handleOpenInBear}
                className="h-7 md:h-8 px-2 md:px-3 text-xs"
                title="In Bear App öffnen"
              >
                <ExternalLink className="h-3 w-3 mr-1" /> 
                <span className="hidden sm:inline">In </span>Bear
              </Button>
              <Button
                data-testid="download-md-bottom-btn"
                variant="outline"
                size="sm"
                onClick={handleDownloadMd}
                className="h-7 md:h-8 px-2 md:px-3 text-xs"
              >
                <Download className="h-3 w-3 mr-1" /> .md
              </Button>
              <Button
                data-testid="copy-bottom-btn"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="h-7 md:h-8 px-2 md:px-3 text-xs"
              >
                {copied ? (
                  <><Check className="h-3 w-3 mr-1" /> <span className="hidden sm:inline">Kopiert</span><span className="sm:hidden">✓</span></>
                ) : (
                  <><Copy className="h-3 w-3 mr-1" /> <span className="hidden sm:inline">Kopieren</span></>
                )}
              </Button>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          {message.timestamp && (
            <span className="text-xs text-muted-foreground">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {!isUser && message.duration && (
            <span className="text-xs text-muted-foreground/70">
              • {message.duration}
            </span>
          )}
        </div>
      </div>
      
      {isUser && (
        <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
          <User className="h-4 w-4 md:h-5 md:w-5 text-secondary-foreground" />
        </div>
      )}
    </div>
  );
}